import { decodeEmbeddingVector, EMBEDDING_PATH, EMBEDDING_VERSION, QUERY_EMBEDDING_TIMEOUT_MS, type EmbeddingKind } from './embedding-protocol'
import { createRelayFetch, insertRelayedTelemetry, type Tel } from '@w/tel'
import type { PageSearchEmbeddings } from './page-search'

export function createServerEmbeddings(input: {
  baseUrl: string
  /** Uninstrumented transport: each call opens its own fetch span and adopts the server span returned in the response
   * (createRelayFetch + insertRelayedTelemetry, as the RPC client does: rpc/src/client/client.ts instrumentRpcFetch). */
  fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>
  canEmbed?: () => boolean
  /** Starts the query deadline; returns its cancel. Defaults to setTimeout. */
  setTimer?: (fire: () => void, ms: number) => () => void
}) {
  const setTimer = input.setTimer ?? ((fire: () => void, ms: number) => { const id = setTimeout(fire, ms); return () => { clearTimeout(id) } })
  const lifetime = new AbortController()
  async function embed(text: string, kind: EmbeddingKind, signal: AbortSignal, tel: Tel) {
    signal.throwIfAborted()
    if (input.canEmbed && !input.canEmbed()) throw new Error('Embeddings require admin access')
    const response = await createRelayFetch({ tel, mode: 'native', fetch: input.fetch })(`${input.baseUrl.replace(/\/$/u, '')}${EMBEDDING_PATH}`, {
      method: 'POST', credentials: 'include', signal,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ text, kind, version: EMBEDDING_VERSION }),
    })
    if (!response.ok) throw new EmbeddingRequestError(response.status, await _failureReason(response))
    insertRelayedTelemetry(response)
    if (response.headers.get('x-embedding-version') !== EMBEDDING_VERSION) throw new Error('Embedding version mismatch')
    const vector = decodeEmbeddingVector(await response.arrayBuffer())
    signal.throwIfAborted()
    return vector
  }
  return {
    canEmbed: input.canEmbed ?? (() => true),
    embedDocument(text: string, tel: Tel, options?: { signal?: AbortSignal }) {
      return embed(text, 'document', options?.signal ? AbortSignal.any([lifetime.signal, options.signal]) : lifetime.signal, tel)
    },
    async embedQuery(text: string, tel: Tel) {
      // Race as well as abort: a stalled transport/body must not hold search open.
      const deadline = new AbortController()
      const signal = AbortSignal.any([lifetime.signal, deadline.signal])
      let cancelTimer: (() => void) | undefined
      const timeout = new Promise<never>((_resolve, reject) => {
        cancelTimer = setTimer(() => {
          const error = new DOMException('Query embedding timed out', 'TimeoutError')
          deadline.abort(error)
          reject(error)
        }, QUERY_EMBEDDING_TIMEOUT_MS)
      })
      try {
        const vector = await Promise.race([embed(text, 'query', signal, tel), timeout])
        return vector
      }
      catch (error) {
        tel.warn('Server query embedding unavailable; local search retained.', { error })
        throw error
      }
      finally { cancelTimer?.() }
    },
    close() { lifetime.abort() },
  }
}

export type ServerEmbeddings = ReturnType<typeof createServerEmbeddings>

/** A non-2xx answer from /api/embeddings, with the server's reason. A 4xx is a warning at the fetch level (tel's
 * INV-TEL-CANCEL); the background job that cannot ever index the page reports its own operation error. */
export class EmbeddingRequestError extends Error {
  override readonly name = 'EmbeddingRequestError'
  readonly details: { status: number; reason: string }
  constructor(readonly status: number, readonly reason: string) {
    super(`Embedding request failed: HTTP ${status}${reason ? ` ${reason}` : ''}`)
    this.details = { status, reason }
  }
  /** The request itself is invalid: resending it gets the same answer (workspace-replication-errors.ts: 4xx terminal, 5xx/0 transient).
   * 401/403 (session, role), 408 and 429 concern the caller or the moment, not this request. */
  get final(): boolean {
    return this.status >= 400 && this.status < 500 && ![401, 403, 408, 429].includes(this.status)
  }
}

/** A refusal's body is text; a server failure's is `{ ok: false, error, telemetry }` (createServer). Either way the
 * returned server span is adopted under this fetch span. */
async function _failureReason(response: Response): Promise<string> {
  const text = (await response.text().catch(() => '')).trim()
  let body: unknown
  try { body = JSON.parse(text) } catch { body = undefined }
  const rest = insertRelayedTelemetry(response, body)
  if (rest && typeof rest === 'object' && 'error' in rest && typeof rest.error === 'string') return rest.error
  return body === undefined ? text : JSON.stringify(rest)
}

export function withNemotronQuery(local: PageSearchEmbeddings, server?: ServerEmbeddings): PageSearchEmbeddings {
  return { get index() { return local.index }, embedQuery: (text, tel) => local.embedQuery(text, tel),
    get embedNemotronQuery() { return server?.canEmbed() ? (text: string, tel: Tel) => server.embedQuery(text, tel) : undefined },
  }
}
